import * as React from "react";
// import axios from "axios";
import { useDispatch } from "react-redux";

// material-ui
import makeStyles from "@material-ui/styles/makeStyles";
import MuiDrawer from "@material-ui/core/Drawer";
import Card from "@material-ui/core/Card";
import Grid from "@material-ui/core/Grid";
import Typography from "@material-ui/core/Typography";
import Divider from "@material-ui/core/Divider";
import TextField from "@material-ui/core/TextField";
import MenuItem from "@material-ui/core/MenuItem";

import Alert from "@material-ui/lab/Alert";

//icons
import IconButton from "@material-ui/core/IconButton";
import ChevronRightIcon from "@material-ui/icons/ChevronRight";
import ActionSend from "@material-ui/icons/Send";
import ActionClose from "@material-ui/icons/Close";

// jaza-soft
import { Button, showNotification } from "jazasoft";

// local
import MuiTable from "../../../../../components/MuiTable";

import { getDistinctValues, isEmpty, isTrue, listFromCsv, toFixedValue } from "../../../../../utils/helpers";
import { useDeepEffect, useDeepMemo, useMutation, useQuery, useSafeState } from "../../../../../hooks";
import { LotType, OrderFlowType, UOM } from "../../../../../constants";
import { InventoryStatus, QcStatus, OState } from "../../../../../utils/types";
import { colorLightGreen, colorOrange, colorTomatoRed } from "../../../../../utils/colors";
import CardHeader from "../../../../../components/CardHeader";
import { getAllocOrderLabel, getCutOrderLabel, getFpoLabel, getGroupOrderLabel } from "../../../../../helpers/orderHelper";
import { getGroupName } from "../../../../grouping/groupingHelper";
import { convert } from "../../../../../helpers/unitConversionHelper";
import ConfirmDialog from "../../../../../components/ConfirmDialog";

const UomCodeFromValue = Object.values(UOM).reduce((acc, uom) => ({ ...acc, [uom.value]: uom.code }), {});

const sanitizeRollInfo = ({
  id,
  rollNo,
  length,
  csv,
  supplierRollNo,
  newGroupId,
  newGroupWidth,
  newGroupWarpPattern,
  newGroupWeftPattern,
  // newGroupCsv,
  newGroupSkewnessGroup
}) => ({
  id,
  rollNo,
  length,
  csv,
  supplierRollNo,
  newGroupId,
  newGroupWidth,
  newGroupWarpPattern,
  newGroupWeftPattern,
  // newGroupCsv,
  newGroupSkewnessGroup
});

const renderQcStatus = value => (
  <span
    style={{
      backgroundColor:
        value === QcStatus.PASS
          ? colorLightGreen.dark
          : value === QcStatus.FAIL
          ? colorTomatoRed.dark
          : value === QcStatus.ON_HOLD
          ? colorOrange.dark
          : "none",
      color: "white",
      padding: "4px 8px",
      borderRadius: 4
    }}>
    {value}
  </span>
);

const renderExternalRollStatus = value => (
  <div
    style={{
      backgroundColor: value === "Selected" ? colorLightGreen.dark : colorOrange.dark,
      color: "white",
      padding: "4px 8px",
      borderRadius: 4,
      textAlign: "center"
    }}>
    {value}
  </div>
);

const useStyles = makeStyles(theme => ({
  drawerPaper: {
    maxWidth: 1000
  },
  marginTop: {
    marginTop: "1.5em"
  },
  drawerHeader: {
    display: "flex",
    width: 540,
    alignItems: "center",
    padding: theme.spacing(0, 1),
    // necessary for content to be below app bar
    ...theme.mixins.toolbar,
    justifyContent: "flex-start"
  },
  content: {
    display: "flex",
    flex: 1,
    flexDirection: "column",
    padding: "1.5em"
  },
  table: {
    "& th": {
      minWidth: 100
    },
    "& td > div": {
      minWidth: 100
    }
  }
}));

const AllocationDrawer = props => {
  const classes = useStyles();
  const { open, order, flowInfo = {}, part, selectedRolls, rollFields, conversionFactorList, onDrawerClose, onRefresh } = props;

  const [allocOrderId, setAllocOrderId] = useSafeState(null);
  const [groupOrderId, setGroupOrderId] = useSafeState(null);
  const [cutOrderId, setCutOrderId] = useSafeState(null);
  const [extRollList, setExtRollList] = useSafeState();
  const [showDialog, setShowDialog] = useSafeState({ open: false, rows: [], onSubmitComplete: null });

  const { data: fpoData = [] } = useQuery(!isEmpty(order?.id) && { url: `fpo`, params: { search: `orderId==${order.id}` } });

  const [mutate, { loading: saving }] = useMutation();
  const dispatch = useDispatch();

  const hasAllocOrder = selectedRolls.some(roll => !isEmpty(roll.allocOrderId));
  const hasGroupOrder = selectedRolls.some(roll => !isEmpty(roll.groupOrderId));

  const { data: externalAppList = [] } = useQuery({ url: `externalApps`, params: { size: 1000 } });

  const extRollOpList = externalAppList
    ?.flatMap(externalApp => externalApp?.integrationOperationList?.map(op => ({ ...op, externalApp })) || [])
    .filter(op => isTrue(op.enabled) && op.entityId === "ExternalRoll");
  const extRollOp = isEmpty(order?.externalIds) ? {} : extRollOpList.find(op => op.externalApp?.appId === "WFX");

  useDeepEffect(() => {
    if (isEmpty(extRollOp) || isEmpty(selectedRolls)) return;

    const headers = JSON.parse(extRollOp?.headers || "{}");
    const authConfig = !isEmpty(extRollOp?.externalApp?.authConfig) ? JSON.parse(extRollOp?.externalApp?.authConfig) : {};
    const options = {
      url: extRollOp.url,
      method: extRollOp.method,
      headers: headers,
      data: {
        AuthKey: authConfig?.apiKey,
        rollInfoList: selectedRolls.map(ri => ({ rollNo: ri.rollNo }))
      }
    };
    // axios
    //   .request(options)
    //   .then(response => {
    //     if (response?.data?.Status === "Success" && !isEmpty(response.data?.ResponseData)) {
    //       setExtRollList(response.data?.ResponseData || []);
    //     } else {
    //       console.log({ options, errorMessage: response.data?.ErrorMsg });
    //     }
    //   })
    //   .catch(error => {
    //     console.log({ error });
    //   });
    const backendOptions = {
      url: "wfx/roll-status",
      method: "post",
      data: options
    };

    mutate(backendOptions, {
      onSuccess: response => {
        if (response?.Status === "Success" && !isEmpty(response?.ResponseData)) {
          setExtRollList(response?.ResponseData || []);
        } else {
          console.log({ options, errorMessage: response?.ErrorMsg });
        }
      },
      onError: error => {
        console.log({ error });
      }
    });
  }, [selectedRolls, extRollOp]);

  React.useEffect(() => {
    if (!open) {
      setGroupOrderId(null);
      setCutOrderId(null);
    }
  }, [open, setCutOrderId, setGroupOrderId]);

  const fpoList = useDeepMemo(() => {
    if (isEmpty(fpoData) || isEmpty(flowInfo)) return [];
    const fpoList = fpoData.filter(
      fpo =>
        listFromCsv(fpo.products).includes(flowInfo.product?.name) &&
        listFromCsv(fpo.styles).includes(flowInfo.style) &&
        listFromCsv(fpo.colors).includes(flowInfo.color)
    );
    return fpoList.map(e => ({ id: `fpo_${e.fpo}`, name: getFpoLabel(e, fpoList, ["color"]) }));
  }, [fpoData, flowInfo]);

  const allocOrderList = useDeepMemo(() => {
    if (isEmpty(flowInfo)) return [];
    const flowList = order.orderFlowList
      .filter(of => of.type === OrderFlowType.ALLOCATION)
      .filter(of => of.productIdList?.includes(flowInfo?.product?.id) && of.styleColorList?.includes(flowInfo.styleColor) && of.part === part)
      .map(of => ({ ...of, name: getAllocOrderLabel(of, order, ["color"]) }));
    if (!isEmpty(flowList)) return flowList;
    return [{ id: `color_${flowInfo.product?.id}_${flowInfo.style}_${flowInfo.color}`, name: `${flowInfo.color}` }];
  }, [order, flowInfo, part, fpoList]);

  useDeepEffect(() => {
    setAllocOrderId(allocOrderList[0]?.id);
  }, [allocOrderList]);

  const groupOrderList = useDeepMemo(() => {
    if (isEmpty(flowInfo)) return [];
    const flowList = order.orderFlowList
      .filter(of => of.type === OrderFlowType.GROUPING)
      .filter(of => of.productIdList?.includes(flowInfo?.product?.id) && of.styleColorList?.includes(flowInfo.styleColor) && of.part === part)
      .map(of => ({ ...of, name: getGroupOrderLabel(of, order, ["fpo", "unit", "color"]) }));
    return flowList;
  }, [order, flowInfo, part, fpoList]);

  const groupOrder =
    hasGroupOrder && getDistinctValues(selectedRolls.map(e => e.groupOrder))?.length === 1
      ? order?.orderFlowList?.find(e => e.id === selectedRolls[0]?.groupOrderId)
      : groupOrderList?.find(e => e.id === groupOrderId);

  const { data: groupOrderFlow = {} } = useQuery(
    !hasGroupOrder && !isEmpty(groupOrderId) && !isNaN(groupOrderId) && { url: `cut-orders/${groupOrderId}` }
  );

  const remoteGroupOrder = !isEmpty(groupOrderId) && groupOrderFlow?.id === groupOrderId ? groupOrderFlow : null;

  const columns = useDeepMemo(() => {
    let groupChoices = [];
    if (!isEmpty(remoteGroupOrder?.groupList)) {
      if (remoteGroupOrder.groupList.some(e => e.type === "FINAL")) {
        groupChoices = remoteGroupOrder.groupList
          .filter(g => g.type === "FINAL")
          .map(g => ({ id: g.id, name: getGroupName(g), groupNo: parseInt(g.name.replace("G", "")) }))
          .sort((a, b) => a.groupNo - b.groupNo);
      } else {
        groupChoices = remoteGroupOrder.groupList
          .map(g => ({ id: g.id, name: getGroupName(g), groupNo: parseInt(g.name.replace("G", "")) }))
          .sort((a, b) => a.groupNo - b.groupNo);
      }
      if (!isEmpty(groupChoices)) {
        groupChoices = [{ id: -1, name: "None" }, ...groupChoices];
      }
    }
    const partObj = flowInfo?.partList?.find(e => e.effPartName === part);
    const isKg = partObj?.uom === UOM.KILOGRAM.value || partObj?.uom === UOM.GRAM.value;

    let cols = [
      { dataKey: "rollNo", title: "Roll No" },
      { dataKey: "qcStatus", title: "QC Status", render: renderQcStatus },
      { dataKey: "width", title: "Cuttable\nWidth" },
      { dataKey: "warpShrPercent", title: "Shrinkage %\n(Length)" },
      { dataKey: "weftShrPercent", title: "Shrinkage %\n(Width)" },
      { dataKey: "shade", title: "Shade" },
      { dataKey: "csv", title: "CSV" },
      { dataKey: "status", title: "Status", render: renderExternalRollStatus },
      { dataKey: "skewness", title: "Skewness %" },
      { dataKey: "newGroupId", title: "Existing \nGroups", inputType: "select-input", choices: groupChoices },
      {
        dataKey: "newGroupWidth",
        title: "New Group \nWidth",
        inputType: "text-input",
        options: { type: "number" },
        disabled: row => !isEmpty(row?.newGroupId) && row.newGroupId !== -1
      },
      {
        dataKey: "newGroupWarpPattern",
        title: "New Group \nWarp Pattern",
        inputType: "text-input",
        options: { type: "number" },
        disabled: row => !isEmpty(row?.newGroupId) && row.newGroupId !== -1
      },
      {
        dataKey: "newGroupWeftPattern",
        title: "New Group \nWeft Pattern",
        inputType: "text-input",
        options: { type: "number" },
        disabled: row => !isEmpty(row?.newGroupId) && row.newGroupId !== -1
      },
      // {
      //   dataKey: "newGroupCsv",
      //   title: "New Group \nCSV",
      //   inputType: "select-input",
      //   disabled: row => !isEmpty(row?.newGroupId) && row.newGroupId !== -1,
      //   choices: Object.values(CSV).map(v => ({ id: v, name: v }))
      // },
      {
        dataKey: "newGroupSkewnessGroup",
        title: "New Group \nSkewness",
        inputType: "text-input",
        options: { type: "number" },
        disabled: row => !isEmpty(row?.newGroupId) && row.newGroupId !== -1
      },
      { dataKey: "extStatus", title: "External\nStatus" },
      {
        dataKey: "fabricQty",
        title: "Fabric Qty",
        render: (value, _, row) =>
          `${toFixedValue(row?.length)} m ${
            isEmpty(partObj?.uom) || partObj?.uom === UOM.METER
              ? ""
              : isKg
              ? `(${toFixedValue(row?.weight, 3)} kg)`
              : `(${toFixedValue(convert({ input: row.length, uomIn: UOM.METER.value, uomOut: partObj.uom, conversionFactorList }))} ${
                  UomCodeFromValue[partObj?.uom]
                })`
          }`
      },
      {
        dataKey: "extStock",
        title: "External\nStock",
        render: (value, _, row) =>
          `${toFixedValue(row?.extStock)} ${
            isEmpty(row?.fabricItem) ? "" : Object.values(UOM)?.find(e => e.value === row?.fabricItem?.uomLength)?.code || ""
          }`
      },
      { dataKey: "warehouse", title: "Warehouse" },
      { dataKey: "extWarehouse", title: "External\nWarehouse" }
    ];

    const fields = [
      ...rollFields,
      "fabricQty",
      "status",
      "newGroupId",
      "newGroupWidth",
      "newGroupWarpPattern",
      "newGroupWeftPattern",
      "newGroupCsv",
      "newGroupSkewnessGroup",
      "extStatus",
      "extStock",
      "warehouse",
      "extWarehouse"
    ];
    cols = cols.filter(c => fields.includes(c.dataKey));

    if (!isEmpty(remoteGroupOrder) && remoteGroupOrder?.groupList?.every(e => e.type === "SHRINKAGE")) {
      cols = cols.filter(c => !["newGroupWidth", "newGroupCsv", "newGroupSkewnessGroup"].includes(c.dataKey));
    }

    if (
      hasGroupOrder ||
      remoteGroupOrder?.stateValue < OState.SHRINKAGE_GROUPING_COMPLETED.stateValue ||
      isEmpty(groupOrderId) ||
      isNaN(groupOrderId)
    ) {
      cols = cols.filter(
        c =>
          !["newGroupId", "newGroupWidth", "newGroupWarpPattern", "newGroupWeftPattern", "newGroupCsv", "newGroupSkewnessGroup"].includes(c.dataKey)
      );
    }

    if (isEmpty(extRollOp)) {
      cols = cols.filter(c => !["extStatus", "extStock", "warehouse", "extWarehouse"].includes(c.dataKey));
    }

    return cols;
  }, [rollFields, hasGroupOrder, remoteGroupOrder, groupOrderId, flowInfo, part, conversionFactorList, extRollOp]);

  const rows = useDeepMemo(() => {
    if (isEmpty(selectedRolls)) return [];
    return selectedRolls.map(e => {
      const externalRoll = !isEmpty(extRollList) ? extRollList?.find(roll => roll.WFXStockMovementNo === e.rollNo) : {};

      const uom = e?.fabricItem?.uomLength;
      const stock = uom === UOM.KILOGRAM.value ? e.weight : convert({ input: e.length, uomIn: UOM.METER.value, uomOut: uom, conversionFactorList });
      const extStock = externalRoll?.StockUnits;
      let status;
      if (isEmpty(extRollOp)) {
        status =
          e.invStatus === InventoryStatus.ISSUED
            ? InventoryStatus.ISSUED
            : (e.plannedInLay || 0) + (e.usedInLay || 0) + (e.usedInPart || 0) > 0
            ? InventoryStatus.PLANNED
            : InventoryStatus.NOT_PLANNED;
      } else {
        status =
          externalRoll?.Status === "Available" && Math.abs(stock - extStock) <= 0.1 && e.currWarehouse === externalRoll?.SiteCode
            ? "Selected"
            : externalRoll?.Status === "Used"
            ? "Used"
            : Math.abs(stock - extStock) > 0.1
            ? "Stock Mismatch"
            : e.currWarehouse !== externalRoll?.SiteCode
            ? "Site Mismatch"
            : "Not Selected";
      }
      return {
        ...e,
        extStatus: externalRoll?.Status || "-",
        extStock: externalRoll?.StockUnits || 0,
        warehouse: e.currWarehouse || "-",
        extWarehouse: externalRoll?.SiteCode || "-",
        status: status
      };
    });
  }, [selectedRolls, extRollList]);

  const cutOrderList = useDeepMemo(() => {
    if (isEmpty(order?.orderFlowList) || isEmpty(flowInfo)) return [];

    const flowList = order.orderFlowList
      .filter(of => of.type === OrderFlowType.CUTPLAN)
      .filter(of => of.productIdList?.includes(flowInfo?.product?.id) && of.styleColorList?.includes(flowInfo.styleColor) && of.part === part);
    const preRunFlowList = flowList
      .filter(of => of.lotType === LotType.PRE_RUN)
      .map(flow => ({ ...flow, name: getCutOrderLabel(flow, order, ["fpo", "unit", "product", "style", "color", "inseam", "lot"]) }));
    const bulkFlowList = flowList
      .filter(of => of.lotType === LotType.BULK && !isEmpty(groupOrder) && of.parentId === groupOrder?.id)
      .map(flow => ({ ...flow, name: getCutOrderLabel(flow, order, ["poRef", "inseam"]) }));
    return [...bulkFlowList, ...preRunFlowList];
  }, [order, flowInfo, groupOrder, rows]);

  const cutOrders = getDistinctValues(rows.map(row => row.cutOrder));
  const currCutOrder = cutOrders?.length === 1 ? rows[0]?.cutOrderFlow : null;
  const finalCutOrderList = cutOrderList.filter(flow => isEmpty(currCutOrder) || currCutOrder.id !== flow.id);

  const errorMessage = useDeepMemo(() => {
    //** Unallocated rolls cannot be allocated for Grouping and Pre-Run together. */
    if (!isEmpty(groupOrderId) && !isEmpty(cutOrderId) && isNaN(groupOrderId)) {
      return `Unallocated rolls cannot be selected for Grouping and Pre-Run together. Hint: Either select Pre-Run Cut Order or Group Order`;
    }

    let message;
    //** Incorrect Selection **//
    // 1. Rolls of multiple Group Order
    // 2. Rolls of multiple Cut Order
    // 3. Issued rolls
    // 4. Planned rolls which is not extra
    // 5. If integrated, then External rolls if they are not available
    const groupOrders = getDistinctValues(rows.map(row => row.groupOrder));
    const cutOrders = getDistinctValues(rows.map(row => row.cutOrder));
    const isIssued = rows.some(roll => roll.status === InventoryStatus.ISSUED);
    const isPlanned = rows.some(roll => roll.status === InventoryStatus.PLANNED);
    const isUnavailable = rows?.some(e => e.status !== "Selected");
    if (groupOrders.length > 1) {
      message = `Incorrect roll selection. Rolls from multiple Group Orders cannot be selected`;
    } else if (cutOrders.length > 1) {
      message = `Incorrect roll selection. Rolls from multiple Cut Orders cannot be selected`;
    } else if (isIssued) {
      message = `Incorrect roll selection. Issued rolls cannot be selected`;
    } else if (isPlanned) {
      message = `Incorrect roll selection. Planned rolls cannot be selected`;
    } else if (!isEmpty(extRollOp)) {
      if (isUnavailable) {
        message = `Roll allocation of these rolls is not possible. Stock and warehouse for some rolls do not match in external system.`;
      }
    }
    return message;
  }, [cutOrderList, flowInfo, selectedRolls, rows, fpoList, groupOrderId, cutOrderId]);

  const onSubmit = (rows, onSubmitComplete) => {
    if (!isEmpty(errorMessage)) {
      dispatch(showNotification(errorMessage, { type: "danger" }));
      return;
    }

    //* Validation *//
    let error = "";
    if (hasAllocOrder) {
      if (isEmpty(groupOrderId) && isEmpty(cutOrderId)) {
        error = "Group Order and(or) Cut Order is required for transferring roll.";
      }
    } else {
      if (isEmpty(allocOrderId) && isEmpty(cutOrderId)) {
        error = "Select Style Color for allocating roll.";
      }
    }
    if (!isEmpty(error)) {
      dispatch(showNotification(error, { type: "danger" }));
      return;
    }

    if (rows.some(e => e.invStatus !== InventoryStatus.UNALLOCATED && (!isEmpty(e.groupOrderId) || !isEmpty(e?.cutOrderId))) && !showDialog?.open) {
      setShowDialog({ open: true, rows, onSubmitComplete });
      return null;
    }

    if (!hasAllocOrder) {
      const orderId = flowInfo.orderId;
      const productId = flowInfo.product?.id;
      const style = flowInfo.style;
      const color = flowInfo.color;

      const options = {
        url: `rolls/allocate`,
        method: "put",
        params: { orderId, productId, style, color, part },
        data: rows.map(sanitizeRollInfo).map(e => ({ ...e, groupOrderId, cutOrderId }))
      };
      mutate(options, {
        onSuccess: () => {
          dispatch(showNotification(`Rolls allocated successfully.`));
          onRefresh && onRefresh();
          onDrawerClose && onDrawerClose();
          setShowDialog({ open: false, rows: [], onSubmitComplete: null });
        },
        onFailure: () => {
          onSubmitComplete && onSubmitComplete(rows);
          setShowDialog({ open: false, rows: [], onSubmitComplete: null });
        }
      });
    } else {
      const orderId = flowInfo.orderId;
      const productId = flowInfo.product?.id;
      const style = flowInfo.style;
      const color = flowInfo.color;

      const options = {
        url: `rolls/transfer`,
        method: "put",
        params: { orderId, productId, style, color, part, groupOrderId: groupOrder?.id, cutOrderId },
        data: rows.map(sanitizeRollInfo)
      };

      mutate(options, {
        onSuccess: () => {
          if (!hasGroupOrder) {
            dispatch(showNotification(`Rolls added successfully.`));
          } else {
            dispatch(showNotification(`Rolls transferred successfully.`));
          }
          onRefresh && onRefresh();
          onDrawerClose && onDrawerClose();
          setShowDialog({ open: false, rows: [], onSubmitComplete: null });
        },
        onFailure: () => {
          onSubmitComplete && onSubmitComplete(rows);
          setShowDialog({ open: false, rows: [], onSubmitComplete: null });
        }
      });
    }
  };

  let ffHandleSubmit;
  const handleSubmitRef = submit => {
    ffHandleSubmit = submit;
  };

  return (
    <div>
      <ConfirmDialog
        open={showDialog.open || false}
        confirmTitle="Already Allocated Rolls!"
        confirmMessage="The selected rolls are already allocated. Do you want to proceed anyway?"
        onConfirm={() => onSubmit(showDialog?.rows, showDialog?.onSubmitComplete)}
        onCancel={_ => setShowDialog({ open: false, rows: [], onSubmitComplete: null })}
        labelConfirm="Proceed"
      />
      <MuiDrawer classes={{ paper: classes.drawerPaper }} anchor="right" open={open} onClose={onDrawerClose}>
        <div className={classes.drawerHeader}>
          <IconButton onClick={onDrawerClose}>
            <ChevronRightIcon />
          </IconButton>
          <div>
            <Typography variant="h5">{hasGroupOrder ? "Roll Transfer" : "Roll Allocation"}</Typography>
          </div>
        </div>
        <Divider />

        <div className={classes.content}>
          {!isEmpty(errorMessage) && (
            <div style={{ marginBottom: "1.5em" }}>
              <Alert severity="error" variant="filled">
                <Typography variant="h6">{errorMessage}</Typography>
              </Alert>
            </div>
          )}

          <Card>
            <CardHeader title={hasGroupOrder ? "Transfer To" : "Allocate To"} />
            <Divider />
            <div style={{ padding: "1.5em" }}>
              <Grid container spacing={2}>
                {!hasAllocOrder && (
                  <Grid item xs={4}>
                    <TextField
                      select
                      label="Style Color"
                      value={allocOrderId}
                      onChange={event => setAllocOrderId(event.target.value)}
                      disabled={true}
                      style={{ width: "100%" }}>
                      <MenuItem value={""}>None</MenuItem>
                      {allocOrderList.map(allocOrder => (
                        <MenuItem key={allocOrder.id} value={allocOrder.id}>
                          {allocOrder.name}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Grid>
                )}
                {!hasGroupOrder && !isEmpty(groupOrderList) && (
                  <Grid item xs={4}>
                    <TextField
                      select
                      label="Group Order"
                      value={groupOrderId}
                      onChange={event => setGroupOrderId(event.target.value)}
                      // disabled={!isEmpty(errorMessage)}
                      style={{ width: "100%" }}>
                      <MenuItem value={""}>None</MenuItem>
                      {groupOrderList.map(groupOrder => (
                        <MenuItem key={groupOrder.id} value={groupOrder.id}>
                          {groupOrder.name}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Grid>
                )}
                {!isEmpty(cutOrderList) && (
                  <Grid item xs={4}>
                    <TextField
                      select
                      label="Cut Order"
                      value={cutOrderId}
                      onChange={event => setCutOrderId(event.target.value)}
                      // disabled={!isEmpty(errorMessage)}
                      style={{ width: "100%" }}>
                      <MenuItem value={""}>None</MenuItem>
                      {finalCutOrderList.map(cutOrder => (
                        <MenuItem key={cutOrder.id} value={cutOrder.id}>
                          {cutOrder.name}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Grid>
                )}
              </Grid>
            </div>
          </Card>

          <div className={classes.marginTop}>
            <MuiTable
              key={groupOrderId}
              tableProps={{ className: classes.table }}
              title={"Rolls"}
              toolbar
              columns={columns}
              rows={rows}
              editable={!hasGroupOrder}
              editing={!hasGroupOrder}
              // editable={!hasGroupOrder && isEmpty(errorMessage)}
              // editing={!hasGroupOrder && isEmpty(errorMessage)}
              onSubmit={(rows, _, onSubmitComplete) => onSubmit(rows, onSubmitComplete)}
              handleSubmitRef={handleSubmitRef}
            />
          </div>

          <div style={{ display: "flex", flex: 1, flexGrow: 1 }}></div>

          {/* Footer */}
          <div className={classes.marginTop}>
            <Button
              label="Submit"
              variant="contained"
              color="primary"
              disabled={!isEmpty(errorMessage) || saving}
              onClick={event => (hasGroupOrder ? onSubmit(rows) : ffHandleSubmit(event))}>
              <ActionSend />
            </Button>
            <Button label="Cancel" variant="outlined" color="primary" onClick={() => onDrawerClose()} style={{ marginLeft: "1em" }}>
              <ActionClose />
            </Button>
          </div>
        </div>
      </MuiDrawer>
    </div>
  );
};

export default AllocationDrawer;
